import type { CoverRequest } from "./schemas/covers.ts";
import { DEFAULT_TIMEZONE, formatSessionTime } from "./time.ts";

/**
 * The words of a cover request (Pilates studio shape, S3), for Desk and the mobile app. Pure, so
 * both apps say the same thing and a test reads each sentence with no screen. `staff` and
 * `staffPlural` are the business's own labels ("instructor", "coaches").
 */

export type CoverWords = { staff: string; staffPlural: string };

export type CoverText = {
  /** One line: what is true now, for the reader. */
  title: string;
  /** The line under it: what happens next. */
  detail: string;
  /** The tone of the badge on a session: waits, done, or closed. */
  tone: "waiting" | "done" | "closed";
};

/** The short word on a session's badge. */
export const COVER_BADGE: Record<CoverRequest["status"], string> = {
  open: "Cover asked",
  awaiting_approval: "Cover to approve",
  taken: "Covered",
  cancelled: "Cover cancelled",
  expired: "Not covered",
};

function first(name: string | null, fallback: string): string {
  const word = name?.trim().split(/\s+/)[0];
  return word ? word : fallback;
}

function count(value: number, one: string, many: string): string {
  return `${value} ${value === 1 ? one : many}`;
}

/** "Reformer, Mon 12 Oct at 18:00". */
export function coverWhen(cover: Pick<CoverRequest, "groupName" | "startsAt">, timeZone: string = DEFAULT_TIMEZONE): string {
  return `${cover.groupName}, ${formatSessionTime(new Date(cover.startsAt), timeZone)}`;
}

export function coverText(cover: CoverRequest, words: CoverWords): CoverText {
  const asker = first(cover.requestedByName, "A team member");
  const taker = first(cover.takenByName, "A team member");
  const offered = count(cover.offeredCount, words.staff, words.staffPlural);
  switch (cover.status) {
    case "open":
      if (cover.offeredToMe) {
        return { title: `${asker} asks for cover`, detail: "Can you take this session? The first yes takes it.", tone: "waiting" };
      }
      if (cover.mine) {
        return {
          title: "You asked for cover",
          detail: `Offered to ${offered}. You stay on the session until a person takes it.`,
          tone: "waiting",
        };
      }
      return {
        title: `${asker} asked for cover`,
        detail:
          cover.declinedCount > 0
            ? `Offered to ${offered}. ${count(cover.declinedCount, "person", "people")} said no. Nobody took it yet.`
            : `Offered to ${offered}. Nobody took it yet.`,
        tone: "waiting",
      };
    case "awaiting_approval":
      if (cover.canDecide) {
        return { title: `${taker} wants to cover ${asker}`, detail: `Approve or refuse. Until you approve, ${asker} stays on the session.`, tone: "waiting" };
      }
      if (cover.takenByMe) return { title: "You said yes", detail: "The owner must approve. Until then, you are not on the session.", tone: "waiting" };
      if (cover.mine) return { title: `${taker} said yes`, detail: "The owner must approve. Until then, you stay on the session.", tone: "waiting" };
      return { title: `${taker} wants to cover ${asker}`, detail: "The owner must approve.", tone: "waiting" };
    case "taken":
      if (cover.takenByMe) return { title: "You cover this session", detail: `${asker} asked for cover.`, tone: "done" };
      if (cover.mine) return { title: `${taker} covers your session`, detail: "You are not on this session now.", tone: "done" };
      return { title: `${taker} covers this session`, detail: `${asker} asked for cover.`, tone: "done" };
    case "cancelled":
      return { title: "Cover request cancelled", detail: "The request was taken back, or the session changed.", tone: "closed" };
    case "expired":
      return { title: "Nobody took the cover", detail: "The session started before a person said yes.", tone: "closed" };
  }
}

/** True while the request still needs a person: a yes, or the owner's decision. */
export function coverIsActive(cover: Pick<CoverRequest, "status">): boolean {
  return cover.status === "open" || cover.status === "awaiting_approval";
}

/** A button on a cover card. Approve and Refuse send `decide`. */
export type CoverActionKind = "take" | "decline" | "cancel" | "approve" | "refuse";

/**
 * The buttons the reader can use on this request, the main one first, from the flags the API sends
 * for the reader: Take it and Not me for an offer, Approve and Refuse for the owner, Cancel request
 * for the person who asked (and the owner). Desk and the app show the same ones.
 */
export function coverButtons(cover: CoverRequest): Array<{ kind: CoverActionKind; label: string }> {
  if (!coverIsActive(cover)) return [];
  const buttons: Array<{ kind: CoverActionKind; label: string }> = [];
  if (cover.status === "open" && cover.offeredToMe) buttons.push({ kind: "take", label: "Take it" }, { kind: "decline", label: "Not me" });
  if (cover.status === "awaiting_approval" && cover.canDecide) buttons.push({ kind: "approve", label: "Approve" }, { kind: "refuse", label: "Refuse" });
  if (cover.canCancel) buttons.push({ kind: "cancel", label: "Cancel request" });
  return buttons;
}

/** What a toast or an alert says after an answer that worked. `cover`: the request as the API sent it back. */
export function coverDoneText(kind: CoverActionKind | "ask", cover: Pick<CoverRequest, "status" | "takenByName"> | undefined): string {
  switch (kind) {
    case "ask":
      return "Cover asked. The others get a push.";
    case "take":
      return cover?.status === "awaiting_approval" ? "You said yes. The owner must approve." : "You cover this session now.";
    case "decline":
      return "You said no. The offer is closed for you.";
    case "cancel":
      return "Cover request cancelled.";
    case "approve":
      return `Approved. ${cover?.takenByName ? first(cover.takenByName, "") : "The person who said yes"} runs the session now.`;
    case "refuse":
      return "Refused. The request is open again for the others.";
  }
}
